import api from "@/lib/api";

// Fetch an attachment as a Blob. For PDFs, force the application/pdf type so browsers
// render them inline (in-tab / native viewer) instead of force-downloading.
async function fetchBlob(url, filename) {
  const res = await api.get(url, { responseType: "blob" });
  let blob = res.data;
  const isPdf = /\.pdf$/i.test(filename || "") || blob.type === "application/pdf";
  if (isPdf && blob.type !== "application/pdf") blob = new Blob([blob], { type: "application/pdf" });
  return blob;
}

// Primary action: open the file inline in a new tab (native PDF viewer where supported).
// The tab is opened synchronously inside the click gesture BEFORE the async fetch, otherwise
// browsers/popup-blockers suppress window.open() that happens after an await (which made "View"
// appear to do nothing). After the blob loads we point the already-open tab at it.
// Only relative API paths may be previewed (prevents using the viewer as an open fetcher).
export const isSafeApiPath = (p) => typeof p === "string" && /^\/[A-Za-z0-9]/.test(p) && !p.includes("..");

// PDF and DOCX open in the shared Top-Recruit viewer tab (authorized fetch, nothing public).
export function openDocumentPreview(url, filename) {
  const q = new URLSearchParams({ src: url, name: filename || "Document" });
  window.open(`/documents/preview?${q}`, "_blank");
}

export async function viewAttachment(url, filename) {
  if (viewableKind(filename, "") === "docx") { openDocumentPreview(url, filename); return; }
  const win = window.open("", "_blank");
  try {
    const blob = await fetchBlob(url, filename);
    const objUrl = window.URL.createObjectURL(blob);
    if (win && !win.closed) {
      win.location.href = objUrl;
    } else {
      // Fallback when the popup was blocked: use a same-gesture-free anchor click.
      const a = document.createElement("a");
      a.href = objUrl; a.target = "_blank"; a.rel = "noopener";
      document.body.appendChild(a); a.click(); a.remove();
    }
    setTimeout(() => window.URL.revokeObjectURL(objUrl), 60000);
  } catch (e) {
    if (win && !win.closed) win.close();
    throw e;
  }
}

// Secondary action: explicit download to disk.
export async function downloadAttachment(url, filename) {
  const res = await api.get(url, { responseType: "blob" });
  const objUrl = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = filename || "download";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(objUrl), 60000);
}

const EXT_MIME = {
  pdf: "application/pdf", png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg",
  gif: "image/gif", webp: "image/webp", svg: "image/svg+xml", bmp: "image/bmp",
  txt: "text/plain", csv: "text/csv",
};

// Classify a file for in-app preview.
export function viewableKind(filename, mime) {
  const ext = (filename || "").split(".").pop().toLowerCase();
  if (ext === "pdf" || mime === "application/pdf") return "pdf";
  if (ext === "docx" || mime === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") return "docx";
  if (["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp"].includes(ext) || (mime || "").startsWith("image/")) return "image";
  if (["txt", "csv"].includes(ext) || (mime || "").startsWith("text/")) return "text";
  return "other";
}

// Fetch an attachment (with auth) and return an object URL whose MIME type is correct for inline
// rendering, so browsers show PDFs/images instead of downloading them.
export async function fetchAttachmentUrl(url, filename) {
  const res = await api.get(url, { responseType: "blob" });
  let blob = res.data;
  const ext = (filename || "").split(".").pop().toLowerCase();
  const want = EXT_MIME[ext];
  if (want && blob.type !== want) blob = new Blob([blob], { type: want });
  return window.URL.createObjectURL(blob);
}
